import React, { createContext, useContext, useState, useRef, useEffect, useCallback, useId } from "react";
import { createPortal } from "react-dom";
import { FIELD_BASE } from "./fieldClasses";

/**
 * Custom select, keyboard and screen-reader operable (WAI-ARIA select-only combobox):
 *
 *   <Select value={v} onValueChange={setV}>
 *     <SelectTrigger id="industry" aria-labelledby="industry-label">   // or <Label htmlFor="industry">
 *       <SelectValue placeholder="Select" />
 *     </SelectTrigger>
 *     <SelectContent>
 *       <SelectItem value="a">A</SelectItem>
 *     </SelectContent>
 *   </Select>
 *
 * Focus stays on the trigger (`role="combobox"`, `aria-expanded`, `aria-controls`,
 * `aria-activedescendant`); the portalled list is `role="listbox"` with `role="option"` children.
 * Keys: ArrowDown/ArrowUp/Enter/Space open; ArrowUp/Down move, Home/End jump, type-ahead
 * highlights, Enter/Space select, Escape closes (focus returns to the trigger), Tab closes.
 */

// Context to share select state between compound components
const SelectContext = createContext(null);

const TYPEAHEAD_RESET_MS = 700;

// Option element ids must be valid id tokens (no spaces) and unique per listbox.
function optionDomId(listboxId, value) {
  const slug = String(value).replace(/[^A-Za-z0-9_-]/g, (ch) => `_${ch.charCodeAt(0).toString(16)}`);
  return `${listboxId}-opt-${slug}`;
}

function isTypeaheadKey(event) {
  return (
    event.key.length === 1 &&
    !event.ctrlKey &&
    !event.metaKey &&
    !event.altKey &&
    event.key !== " "
  );
}

export function Select({ children, value, onValueChange, className = "" }) {
  const [isOpen, setIsOpenState] = useState(false);
  // Highlighted option (string key), driven by the keyboard or mouse hover.
  const [activeKey, setActiveKey] = useState(null);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const listboxRef = useRef(null);
  // String key -> original option value, so onValueChange receives what the consumer passed.
  const optionValuesRef = useRef(new Map());
  const typeaheadRef = useRef({ buffer: "", at: 0 });
  const pendingTypeaheadRef = useRef("");
  const [triggerRect, setTriggerRect] = useState(null);
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const defaultTriggerId = `${baseId}-trigger`;

  const setIsOpen = useCallback(
    (next) => {
      setIsOpenState(next);
      setActiveKey(next && value != null && value !== "" ? String(value) : null);
      if (!next) pendingTypeaheadRef.current = "";
    },
    [value]
  );

  const closeAndFocusTrigger = useCallback(() => {
    setIsOpen(false);
    triggerRef.current?.focus();
  }, [setIsOpen]);

  const registerOption = useCallback((key, original) => {
    optionValuesRef.current.set(key, original);
    return () => {
      optionValuesRef.current.delete(key);
    };
  }, []);

  // Enabled options in DOM order (the list is portalled, so the DOM is the source of order).
  const getOptions = useCallback(() => {
    const root = listboxRef.current;
    if (!root) return [];
    return Array.from(root.querySelectorAll('[role="option"]:not([aria-disabled="true"])')).map(
      (el) => ({ key: el.dataset.value ?? "", label: (el.textContent || "").trim().toLowerCase() })
    );
  }, []);

  const selectKey = useCallback(
    (key) => {
      if (key == null) return;
      const original = optionValuesRef.current.has(key) ? optionValuesRef.current.get(key) : key;
      onValueChange?.(original);
      closeAndFocusTrigger();
    },
    [onValueChange, closeAndFocusTrigger]
  );

  const moveActive = useCallback(
    (delta) => {
      const options = getOptions();
      if (!options.length) return;
      const index = options.findIndex((o) => o.key === activeKey);
      let next;
      if (index === -1) next = delta > 0 ? 0 : options.length - 1;
      else next = Math.min(options.length - 1, Math.max(0, index + delta));
      setActiveKey(options[next].key);
    },
    [getOptions, activeKey]
  );

  const jumpActive = useCallback(
    (toEnd) => {
      const options = getOptions();
      if (!options.length) return;
      setActiveKey(options[toEnd ? options.length - 1 : 0].key);
    },
    [getOptions]
  );

  // Type-ahead: letters typed within TYPEAHEAD_RESET_MS accumulate; a repeated single letter
  // cycles through options starting with it.
  const typeahead = useCallback(
    (char) => {
      const options = getOptions();
      if (!options.length) return;
      const now = Date.now();
      const state = typeaheadRef.current;
      const buffer = now - state.at < TYPEAHEAD_RESET_MS ? state.buffer + char : char;
      typeaheadRef.current = { buffer: buffer.toLowerCase(), at: now };
      const query = typeaheadRef.current.buffer;
      const startIndex = options.findIndex((o) => o.key === activeKey);
      const repeated = query.length > 1 && query.split("").every((c) => c === query[0]);
      const needle = repeated ? query[0] : query;
      const from = repeated || query.length === 1 ? startIndex + 1 : Math.max(startIndex, 0);
      for (let step = 0; step < options.length; step += 1) {
        const candidate = options[(from + step) % options.length];
        if (candidate.label.startsWith(needle)) {
          setActiveKey(candidate.key);
          return;
        }
      }
    },
    [getOptions, activeKey]
  );

  const onTriggerKeyDown = useCallback(
    (event) => {
      const { key } = event;
      if (!isOpen) {
        if (key === "ArrowDown" || key === "ArrowUp" || key === "Enter" || key === " ") {
          event.preventDefault();
          setIsOpen(true);
          return;
        }
        if (isTypeaheadKey(event)) {
          event.preventDefault();
          pendingTypeaheadRef.current = key;
          setIsOpen(true);
        }
        return;
      }
      switch (key) {
        case "ArrowDown":
          event.preventDefault();
          moveActive(1);
          break;
        case "ArrowUp":
          event.preventDefault();
          moveActive(-1);
          break;
        case "Home":
          event.preventDefault();
          jumpActive(false);
          break;
        case "End":
          event.preventDefault();
          jumpActive(true);
          break;
        case "Enter":
        case " ":
          event.preventDefault();
          if (activeKey != null) selectKey(activeKey);
          else closeAndFocusTrigger();
          break;
        case "Escape":
          event.preventDefault();
          event.stopPropagation();
          closeAndFocusTrigger();
          break;
        case "Tab":
          setIsOpen(false);
          break;
        default:
          if (isTypeaheadKey(event)) {
            event.preventDefault();
            typeahead(key);
          }
      }
    },
    [isOpen, setIsOpen, moveActive, jumpActive, activeKey, selectKey, closeAndFocusTrigger, typeahead]
  );

  // Close dropdown when clicking outside (the trigger or the portalled list)
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = containerRef.current && containerRef.current.contains(event.target);
      const inList = listboxRef.current && listboxRef.current.contains(event.target);
      if (!inTrigger && !inList) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      // Use setTimeout to avoid closing immediately after opening
      const timer = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);

      return () => {
        clearTimeout(timer);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    return undefined;
  }, [isOpen, setIsOpen]);

  // Escape closes the list even when focus has left the trigger (e.g. after a scrollbar click).
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeAndFocusTrigger();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, closeAndFocusTrigger]);

  // A letter typed on the closed trigger opens the list and highlights the first match once the
  // list has mounted (it renders after the trigger rect is measured).
  useEffect(() => {
    if (!isOpen || !triggerRect || !pendingTypeaheadRef.current) return;
    const char = pendingTypeaheadRef.current;
    pendingTypeaheadRef.current = "";
    typeahead(char);
  }, [isOpen, triggerRect, typeahead]);

  // Keep the highlighted option in view while arrowing through a long list.
  useEffect(() => {
    if (!isOpen || activeKey == null || typeof document === "undefined") return;
    const el = document.getElementById(optionDomId(listboxId, activeKey));
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "nearest" });
  }, [isOpen, activeKey, listboxId]);

  const updateRect = useCallback(() => {
    if (containerRef.current) {
      setTriggerRect(containerRef.current.getBoundingClientRect());
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    updateRect();
    let rafId = null;
    let ticking = false;
    const handle = () => {
      if (ticking) return;
      ticking = true;
      rafId = requestAnimationFrame(() => {
        updateRect();
        ticking = false;
      });
    };
    window.addEventListener("resize", handle);
    window.addEventListener("scroll", handle, { passive: true, capture: true });
    return () => {
      window.removeEventListener("resize", handle);
      window.removeEventListener("scroll", handle, true);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [isOpen, updateRect]);
  
  return (
    <SelectContext.Provider
      value={{
        value,
        onValueChange,
        isOpen,
        setIsOpen,
        triggerRect,
        updateRect,
        activeKey,
        setActiveKey,
        selectKey,
        registerOption,
        onTriggerKeyDown,
        triggerRef,
        listboxRef,
        listboxId,
        defaultTriggerId,
      }}
    >
      <div ref={containerRef} className={`relative w-full ${className}`}>
        {children}
      </div>
    </SelectContext.Provider>
  );
}

export function SelectTrigger({
  children,
  className = "",
  style,
  disabled,
  valueContainerClassName = "",
  id,
  ...buttonProps
}) {
  const context = useContext(SelectContext);
  if (!context) {
    throw new Error('SelectTrigger must be used within Select');
  }

  const { isOpen, setIsOpen, activeKey, onTriggerKeyDown, triggerRef, listboxId, defaultTriggerId } = context;
  const triggerId = id || defaultTriggerId;

  return (
    <button
      {...buttonProps}
      id={triggerId}
      ref={triggerRef}
      type="button"
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      aria-controls={isOpen ? listboxId : undefined}
      aria-activedescendant={isOpen && activeKey != null ? optionDomId(listboxId, activeKey) : undefined}
      disabled={disabled}
      onKeyDown={(e) => {
        if (disabled) return;
        onTriggerKeyDown(e);
        buttonProps.onKeyDown?.(e);
      }}
      onClick={(e) => {
        if (disabled) return;
        e.stopPropagation();
        setIsOpen(!isOpen);
        buttonProps.onClick?.(e);
      }}
      className={`${FIELD_BASE.replace('border-white/30', '')} w-full min-w-0 max-w-full justify-between flex items-center pr-8 cursor-pointer min-h-[48px] text-base rounded-xl border text-left disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
      style={{ 
        fontSize: 'max(16px, 1rem)',
        borderColor: 'rgba(96, 165, 250, 0.3)',
        borderWidth: '1px',
        color: '#4B5563',
        ...style
      }}
    >
      <span className={`flex-1 min-w-0 text-left truncate pr-2 ${valueContainerClassName}`}>
        {children}
      </span>
      <svg
        className={`w-5 h-5 ml-2 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        style={{ color: '#60A5FA' }}
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );
}

export function SelectValue({ placeholder, className = "", children }) {
  const context = useContext(SelectContext);
  if (!context) {
    return <span className={`text-[#94A3B8] ${className}`}>{placeholder}</span>;
  }
  
  const { value } = context;
  
  // If children are provided, use them (for custom display)
  if (children) {
    return (
      <span className={`${value ? 'text-[#4B5563]' : 'text-[#94A3B8]'} block w-full text-left truncate ${className}`}>
        {children}
      </span>
    );
  }
  
  return (
    <span className={`${value ? 'text-[#4B5563]' : 'text-[#94A3B8]'} block w-full text-left truncate ${className}`}>
      {value || placeholder}
    </span>
  );
}

export function SelectContent({ children, className = "" }) {
  const context = useContext(SelectContext);
  if (!context) return null;
  
  const { isOpen, triggerRect, listboxRef, listboxId, defaultTriggerId, triggerRef } = context;

  if (!isOpen || !triggerRect) return null;

  const labelledBy = triggerRef.current?.id || defaultTriggerId;

  const dropdown = (
    <div
      ref={listboxRef}
      id={listboxId}
      role="listbox"
      aria-labelledby={labelledBy}
      tabIndex={-1}
      className={`z-[9999] mt-1 bg-white/70 backdrop-blur-xl border border-white/60 rounded-xl shadow-2xl max-h-60 overflow-y-auto overflow-x-hidden ${className}`}
      style={{
        position: 'fixed',
        top: triggerRect.bottom + 6,
        left: triggerRect.left,
        width: triggerRect.width,
        isolation: 'isolate',
        pointerEvents: 'auto',
        userSelect: 'auto',
        scrollbarWidth: 'thin',
        scrollbarGutter: 'stable'
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
  
  if (typeof document === "undefined") {
    return dropdown;
  }

  return createPortal(dropdown, document.body);
}

export function SelectItem({ value, children, className = "", disabled = false }) {
  const context = useContext(SelectContext);
  const key = String(value);
  const registerOption = context?.registerOption;

  // Remember the original value so keyboard selection reports it unchanged.
  useEffect(() => {
    if (!registerOption) return undefined;
    return registerOption(key, value);
  }, [registerOption, key, value]);

  if (!context) {
    return <option value={value} className={className} disabled={disabled}>{children}</option>;
  }

  const { value: selectedValue, selectKey, activeKey, setActiveKey, listboxId } = context;
  const isSelected = selectedValue === value;
  const isActive = activeKey === key;

  return (
    <div
      id={optionDomId(listboxId, key)}
      role="option"
      aria-selected={isSelected}
      aria-disabled={disabled || undefined}
      data-value={key}
      data-active={isActive ? "true" : undefined}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled) return;
        selectKey(key);
      }}
      onMouseDown={(e) => {
        // Keep focus on the trigger so the combobox keeps receiving keys.
        e.preventDefault();
        e.stopPropagation();
      }}
      onMouseEnter={() => {
        if (!disabled) setActiveKey(key);
      }}
      className={`
        px-4 py-3 min-h-[48px] flex items-center text-base transition-colors select-none
        ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
        ${isSelected
          ? 'bg-white/50 font-bold text-slate-900'
          : isActive
            ? 'bg-white/50 text-slate-900 font-medium'
            : 'bg-transparent text-slate-900 hover:bg-white/40 font-medium'
        }
        ${className}
      `}
      style={{ 
        fontSize: 'max(16px, 1rem)',
        pointerEvents: 'auto',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none'
      }}
    >
      {children}
    </div>
  );
}
